import React from 'react';
import { useTranslation } from 'react-i18next';

/**
 * AI Insights widget — sentiment-aware insight cards.
 *
 * Each insight renders as a tinted card whose accent colour reflects its tone
 * (positive=green, negative=red, neutral=teal). When the title carries a metric
 * after a colon (e.g. "EBARZA: +101.0% LFL") the metric is pulled into a pill so
 * the headline number reads at a glance. The store name is followed by its
 * property (mall) name, and the recommendation drops to its own action line
 * with a drill-down button (View tenant / View flight risk / View analytics).
 */

const TONES = {
    positive:  { accent: 'var(--v3-green, #10B981)', glow: 'var(--v3-green-glow, rgba(16,185,129,0.15))', glyph: '▲' },
    negative:  { accent: 'var(--v3-red, #EF4444)',   glow: 'var(--v3-red-glow, rgba(239,68,68,0.15))',   glyph: '▼' },
    neutral:   { accent: 'var(--v3-teal, #06B6D4)',  glow: 'var(--v3-teal-glow, rgba(6,182,212,0.15))',  glyph: '◆' },
    // Hero tone for the portfolio productivity-upside headline (all-property users).
    highlight: { accent: 'var(--v3-amber, #F59E0B)', glow: 'var(--v3-amber-glow, rgba(245,158,11,0.15))', glyph: '✦' },
};

/**
 * Map a backend `action` descriptor to a V3 route. Returns null when the action
 * is missing or unroutable, so the widget simply omits the button.
 */
function actionPath(action) {
    if (!action) return null;
    const base = process.env.PUBLIC_URL || '';
    if (action.type === 'tenant' && action.tenantId) {
        return `${base}/v3/tenant?tenants=${encodeURIComponent(action.tenantId)}`;
    }
    if (action.type === 'command') return `${base}/v3/command`;
    if (action.type === 'analytics') return `${base}/v3/analytics`;
    return null;
}

export default function AIInsightsWidget({ data }) {
    const { t } = useTranslation();
    if (!data?.data) return null;
    const d = data.data;
    const items = d.insights || [];

    const bullets = items.length > 0 ? items : parseTextToBullets(d.text);

    if (bullets.length === 0) {
        return (
            <div style={{ padding: '4px 18px 14px' }}>
                <div className="v3-body" style={{ padding: '10px 0', color: 'var(--v3-text-muted)' }}>
                    {t('No insights available yet.')}
                </div>
            </div>
        );
    }

    return (
        <div
            style={{
                // Fill the card body and scroll internally when the insight cards
                // exceed the widget's grid height — otherwise WidgetCard's
                // `overflow:hidden` body clips the last card.
                height: '100%',
                minHeight: 0,
                overflowY: 'auto',
                boxSizing: 'border-box',
                scrollbarWidth: 'thin',
                padding: '4px 14px 12px',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
            }}
        >
            {bullets.map((item, i) => {
                const tone = TONES[resolveTone(item)] || TONES.neutral;
                const { name, metric } = splitTitle(item.title);
                const href = actionPath(item.action);
                return (
                    <div
                        key={i}
                        style={{
                            display: 'flex',
                            gap: 10,
                            padding: '10px 12px',
                            borderRadius: 10,
                            background: tone.glow,
                            border: '1px solid var(--v3-glass-border)',
                            borderLeft: `3px solid ${tone.accent}`,
                        }}
                    >
                        <span
                            aria-hidden="true"
                            style={{ color: tone.accent, fontSize: 10, lineHeight: '18px', flexShrink: 0 }}
                        >
                            {tone.glyph}
                        </span>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            {/* Headline: name + optional metric pill */}
                            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                {name && (
                                    <span className="v3-title" style={{ flex: 1, minWidth: 0 }}>
                                        {renderInline(name)}
                                    </span>
                                )}
                                {metric && (
                                    <span
                                        style={{
                                            flexShrink: 0,
                                            fontSize: 11,
                                            fontWeight: 700,
                                            letterSpacing: '0.01em',
                                            padding: '2px 8px',
                                            borderRadius: 999,
                                            color: tone.accent,
                                            background: tone.glow,
                                            border: `1px solid ${tone.accent}`,
                                        }}
                                    >
                                        {renderInline(metric)}
                                    </span>
                                )}
                            </div>
                            {item.property && (
                                <div
                                    className="v3-body"
                                    style={{ marginTop: 2, fontSize: 11, color: 'var(--v3-text-dim)', display: 'flex', alignItems: 'center', gap: 4 }}
                                >
                                    <span aria-hidden="true">⌖</span>
                                    {renderInline(item.property)}
                                </div>
                            )}
                            {item.description && (
                                <div className="v3-body" style={{ marginTop: 3, color: 'var(--v3-text-muted)' }}>
                                    {renderInline(item.description)}
                                </div>
                            )}
                            {(item.recommendation || href) && (
                                <div
                                    style={{
                                        marginTop: 7,
                                        paddingTop: 7,
                                        borderTop: '1px solid var(--v3-glass-border)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: 8,
                                    }}
                                >
                                    {item.recommendation && (
                                        <div className="v3-body" style={{ flex: 1, minWidth: 0, fontSize: 11, color: tone.accent }}>
                                            <span aria-hidden="true" style={{ marginRight: 5 }}>→</span>
                                            {t('Consider')} <strong>{renderInline(item.recommendation)}</strong>.
                                        </div>
                                    )}
                                    {href && (
                                        <button
                                            type="button"
                                            // Full-page navigation (not SPA navigate): the target section has
                                            // its own filter provider; a hard load guarantees the deep-linked
                                            // tenant/date hydrates cleanly without two providers racing on the URL.
                                            onClick={() => window.location.assign(href)}
                                            title={t(item.action.label)}
                                            style={{
                                                flexShrink: 0,
                                                cursor: 'pointer',
                                                fontSize: 11,
                                                fontWeight: 600,
                                                padding: '3px 10px',
                                                borderRadius: 6,
                                                color: tone.accent,
                                                background: 'transparent',
                                                border: `1px solid ${tone.accent}`,
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {t(item.action.label)} →
                                        </button>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                );
            })}
        </div>
    );
}

/**
 * Resolve an insight's sentiment tone. Prefers the backend-supplied `tone`
 * field; falls back to inferring from free-text content (legacy / text_card
 * payloads that don't carry structured tone).
 */
function resolveTone(item) {
    if (['positive', 'negative', 'neutral', 'highlight'].includes(item.tone)) {
        return item.tone;
    }
    const hay = `${item.title || ''} ${item.description || ''}`.toLowerCase();
    if (/synergy|correlat/.test(hay)) return 'neutral';
    if (/danger|under[-\s]?perform|risk|declin|drop|-\d/.test(hay)) return 'negative';
    if (/out[-\s]?perform|\+\d|growth|spike|strong/.test(hay)) return 'positive';
    return 'neutral';
}

/**
 * Split a "Name: metric" title into its name and trailing metric.
 * Titles without a colon (e.g. "3 tenants in danger zone") return the whole
 * string as the name and a null metric.
 */
function splitTitle(title) {
    if (!title) return { name: '', metric: null };
    const idx = title.indexOf(':');
    if (idx === -1) return { name: title.trim(), metric: null };
    const metric = title.slice(idx + 1).trim();
    return { name: title.slice(0, idx).trim(), metric: metric || null };
}

function decodeEntities(str) {
    if (!str) return '';
    const txt = document.createElement('textarea');
    txt.innerHTML = str;
    return txt.value;
}

/**
 * Split a single markdown-ish text blob into structured bullets.
 * Recognises lines starting with •, -, or * as bullet items.
 * Within each line, the leading **bold** segment becomes the title,
 * the remainder becomes the description.
 */
function parseTextToBullets(text) {
    if (!text) return [];
    const decoded = decodeEntities(text);

    // Split on newlines first; if the API returns a single line with • separators, fall back to that.
    let lines = decoded.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
    if (lines.length === 1 && lines[0].includes('•')) {
        lines = lines[0].split('•').map(l => l.trim()).filter(Boolean);
    }

    return lines
        .map(l => l.replace(/^[•\-*]\s*/, ''))
        .map(line => {
            const m = line.match(/^\*\*([^*]+)\*\*\s*[—\-:]?\s*(.*)$/);
            if (m) return { title: m[1].trim(), description: m[2].trim() };
            return { title: '', description: line };
        });
}

/**
 * Render inline markdown bold (**text**) inside a string,
 * also decoding HTML entities. Returns React fragment.
 */
function renderInline(str) {
    if (!str) return null;
    const decoded = decodeEntities(String(str));
    const parts = decoded.split(/(\*\*[^*]+\*\*)/g);
    return parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return <React.Fragment key={i}>{part}</React.Fragment>;
    });
}
